import {
    OPTION_PICKER_DROPDOWN_MAX_HEIGHT,
    type OptionPickerAnchorRect,
} from '@/components/molecules/optionPicker/OptionPickerModal.types';
import {
    readViewportMetrics,
    visualPxToLayoutPx,
} from '@/core/utils/viewportMetrics';
import { BREAKPOINTS, getBreakpointWidth } from '@/core/utils/responsive';
import type { RefObject } from 'react';
import type { View } from 'react-native';

const DROPDOWN_GAP = 4;

interface DomRectLike {
    left: number;
    top: number;
    width: number;
    height: number;
}

function readTriggerRect(node: View): DomRectLike | null {
    const withDomRect = node as View & {
        getBoundingClientRect?: () => DomRectLike;
    };
    if (typeof withDomRect.getBoundingClientRect === 'function') {
        return withDomRect.getBoundingClientRect();
    }
    return null;
}

function measureTriggerRect(
    node: View,
    onMeasured: (rect: DomRectLike) => void,
): void {
    const domRect = readTriggerRect(node);
    if (domRect) {
        onMeasured(domRect);
        return;
    }

    if (!node.measureInWindow) {
        return;
    }

    node.measureInWindow((x, y, width, height) => {
        onMeasured({ left: x, top: y, width, height });
    });
}

/**
 * On desktop web, measure the FormPicker trigger before opening the menu.
 * Converts visual coords to layout coords (html zoom) so the menu aligns with the field.
 */
export function measureOptionPickerAnchor(
    ref: RefObject<View | null>,
    onWithAnchor: (anchor: OptionPickerAnchorRect) => void,
    onWithoutAnchor: () => void,
): void {
    if (getBreakpointWidth() < BREAKPOINTS.PHONE) {
        onWithoutAnchor();
        return;
    }

    const node = ref.current;
    if (!node) {
        onWithoutAnchor();
        return;
    }

    measureTriggerRect(node, (rect) => {
        const x = visualPxToLayoutPx(rect.left);
        const y = visualPxToLayoutPx(rect.top);
        const width = visualPxToLayoutPx(rect.width);
        const height = visualPxToLayoutPx(rect.height);

        const layoutViewportH = readViewportMetrics().layoutHeight;
        const belowTop = y + height + DROPDOWN_GAP;
        const opensUpward =
            belowTop + OPTION_PICKER_DROPDOWN_MAX_HEIGHT > layoutViewportH - 8;

        onWithAnchor({
            x: Math.round(x),
            y: Math.round(y),
            width: Math.round(width),
            height: Math.round(height),
            opensUpward,
        });
    });
}
